import React, { useState } from "react";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, CheckCircle2 } from "lucide-react";
import client from "@/lib/api";
import { Button } from "@/components/ui/button";

const TARGETS = ["full_name", "given_name", "surname", "fusion_id", "passport_number", "nationality",
  "date_of_birth", "mobile", "email", "position", "work_location", "passport_expiry_date"];

export default function ImportCandidates() {
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const onFile = async (file) => {
    if (!file) return;
    setLoading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await client.post("/import/candidates/preview", fd);
      setPreview(data);
      // auto-map matching columns
      const m = {};
      TARGETS.forEach((t) => {
        const match = data.columns.find((c) => c.toLowerCase().replace(/[^a-z]/g, "") === t.replace(/[^a-z]/g, "")
          || c.toLowerCase().includes(t.split("_")[0]));
        if (match) m[t] = match;
      });
      setMapping(m);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not read file");
    } finally { setLoading(false); }
  };

  const commit = async () => {
    setLoading(true);
    try {
      const { data } = await client.post("/import/candidates/commit", { rows: preview.rows, mapping });
      setResult(data);
      toast.success(`Imported ${data.imported} candidates`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-5xl">
      <h1 className="font-display text-2xl font-bold text-[#0A192F] flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-[#0088FF]" /> Excel Import — Candidates</h1>
      <p className="text-sm text-slate-400 mb-5">Upload a KSA / Saudi tracker (.xlsx or .csv). Preview, map columns, then import with duplicate detection.</p>

      {!preview ? (
        <label className="block border-2 border-dashed border-slate-300 rounded-2xl p-12 text-center cursor-pointer hover:border-[#0088FF] bg-white" data-testid="import-dropzone">
          <Upload className="h-10 w-10 mx-auto text-slate-400 mb-3" />
          <div className="font-medium text-[#0A192F]">{loading ? "Reading..." : "Click to upload Excel / CSV"}</div>
          <div className="text-xs text-slate-400 mt-1">KSA TRACKER.xlsx, SAUDI ARABIA.xlsx, or any candidate sheet</div>
          <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => onFile(e.target.files[0])} />
        </label>
      ) : result ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
          <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto" />
          <h3 className="font-display text-2xl font-bold text-[#0A192F] mt-4">Import Complete</h3>
          <div className="grid grid-cols-4 gap-4 mt-6 max-w-xl mx-auto">
            {[["Total", result.total], ["Imported", result.imported], ["Duplicates", result.duplicates], ["Invalid", result.invalid]].map(([l, v]) => (
              <div key={l} className="bg-slate-50 rounded-xl p-4"><div className="font-display text-2xl font-bold text-[#0A192F]">{v}</div><div className="text-xs text-slate-500">{l}</div></div>
            ))}
          </div>
          <Button className="mt-6 bg-[#0088FF] hover:bg-[#0070E0]" onClick={() => { setPreview(null); setResult(null); }}>Import Another File</Button>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <h3 className="font-display font-semibold text-[#0A192F] mb-3">Column Mapping ({preview.total_rows} rows found)</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {TARGETS.map((t) => (
                <div key={t}>
                  <label className="text-xs text-slate-500 capitalize">{t.replace(/_/g, " ")}</label>
                  <select data-testid={`map-${t}`} value={mapping[t] || ""} onChange={(e) => setMapping({ ...mapping, [t]: e.target.value })} className="w-full h-9 px-2 rounded-md border border-input bg-background text-sm">
                    <option value="">— skip —</option>
                    {preview.columns.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 text-sm font-medium text-slate-600">Preview (first {preview.rows.length} rows)</div>
            <div className="overflow-x-auto max-h-64">
              <table className="w-full text-xs">
                <thead className="bg-slate-50"><tr>{preview.columns.map((c) => <th key={c} className="text-left px-3 py-2 whitespace-nowrap">{c}</th>)}</tr></thead>
                <tbody>{preview.rows.slice(0, 8).map((r, i) => <tr key={i} className="border-t border-slate-100">{preview.columns.map((c) => <td key={c} className="px-3 py-2 whitespace-nowrap">{r[c]}</td>)}</tr>)}</tbody>
              </table>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setPreview(null)}>Cancel</Button>
            <Button data-testid="import-commit" disabled={loading} onClick={commit} className="bg-[#0088FF] hover:bg-[#0070E0]">{loading ? "Importing..." : "Import Candidates"}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
